import { useEffect, useRef } from "react";
import { skipTransientApiErrors } from "../../api/fetch";
import { followSupportTicket } from "../../api/support-stream";
import {
	fetchSupportTicket,
	SupportAiSuggestion,
	SupportMessage,
	SupportTicket,
} from "../../api/support-tickets";

/**
 * Follow one conversation for as long as it is on screen.
 *
 * Both surfaces use this: the staff thread at /support/<id>, and the
 * reporter's bubble when its panel is open on a conversation. They had
 * separate polls before and would have grown separate stream code the same
 * way; the connection lifecycle is the fiddly part and belongs in one place.
 *
 * `isActive` is the whole concurrency story. The bubble is mounted on every
 * page in ODI, so a stream tied to mounting would hold one connection per
 * open tab. Tied to the panel actually being open, it holds one per person
 * currently reading a support conversation.
 *
 * The starting id is read once, when the stream opens, deliberately: it must
 * not be a dependency, or every arriving message would tear the connection
 * down and build it again.
 */
export interface SupportTicketStreamOptions {
	ticketId: number | null;
	/** Highest message id already on screen; the server replays what follows. */
	afterMessageId: number;
	isActive: boolean;
	onMessage: (message: SupportMessage) => void;
	onSuggestion?: (suggestion: SupportAiSuggestion) => void;
	/** The conversation was named after it was opened — the AI label. */
	onLabel?: (label: string) => void;
	/**
	 * The whole ticket, re-read every time the stream says `ready`: on connect
	 * and on each reconnect. The server ends every stream after
	 * SUPPORT_STREAM_MAX_SECONDS, and its catch-up replays messages only — a
	 * draft, the label, or a picture added to an older
	 * message that landed while the connection was down reaches the screen
	 * through this re-read and nothing else. Fold it in with
	 * `mergeRefetchedTicket`.
	 */
	onTicket: (ticket: SupportTicket) => void;
}

/** Everything the read carries, plus held items it did not include yet. */
function mergeById<TItem extends { id: number }>(
	read: TItem[],
	held: TItem[]
): TItem[] {
	const readIds = new Set(read.map((item) => item.id));
	return [...read, ...held.filter((item) => !readIds.has(item.id))].sort(
		(a, b) => a.id - b.id
	);
}

/**
 * Fold a re-read ticket into the one on screen.
 *
 * The read wins for everything it carries, but a message, draft or research
 * turn the stream delivered after the read was taken is kept rather than
 * wiped by the older snapshot. A ticket with another id is left alone: the
 * reader moved to a different conversation while the read was in flight.
 */
export function mergeRefetchedTicket(
	held: SupportTicket,
	read: SupportTicket
): SupportTicket {
	if (held.id !== read.id) return held;
	return {
		...read,
		messages: mergeById(read.messages, held.messages),
		ai_suggestions: mergeById(read.ai_suggestions ?? [], held.ai_suggestions ?? []),
		research_turns: mergeById(read.research_turns ?? [], held.research_turns ?? []),
	};
}

export function useSupportTicketStream({
	ticketId,
	afterMessageId,
	isActive,
	onMessage,
	onSuggestion,
	onLabel,
	onTicket,
}: SupportTicketStreamOptions): void {
	const onMessageRef = useRef(onMessage);
	onMessageRef.current = onMessage;
	const onSuggestionRef = useRef(onSuggestion);
	onSuggestionRef.current = onSuggestion;
	const onLabelRef = useRef(onLabel);
	onLabelRef.current = onLabel;
	const onTicketRef = useRef(onTicket);
	onTicketRef.current = onTicket;
	const afterRef = useRef(afterMessageId);
	afterRef.current = afterMessageId;

	useEffect(() => {
		if (!isActive || ticketId === null) return;
		const controller = new AbortController();
		void followSupportTicket({
			ticketId,
			afterMessageId: afterRef.current,
			onMessage: (message) => onMessageRef.current(message),
			onSuggestion: (suggestion) => onSuggestionRef.current?.(suggestion),
			onLabel: (label) => onLabelRef.current?.(label),
			// The first `ready` repeats the caller's own load, and is what
			// covers anything that moved between that load and the subscription.
			onReady: () => {
				void fetchSupportTicket(ticketId)
					.then((row) => onTicketRef.current(row))
					.catch(skipTransientApiErrors);
			},
			signal: controller.signal,
		});
		return () => controller.abort();
		// Only the conversation and whether it is on screen may restart it.
	}, [ticketId, isActive]);
}
